import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { deleteProductReview, fetchProductReviewsList } from "../../shared/productChildMastersApi";
import { IconPlus } from "../../shared/toolbarIcons";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

export function ProductReviewsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [iRes, pRes, cRes] = await Promise.all([fetchProductReviewsList(), apiGet("/api/Products"), apiGet("/api/Customers")]);
    if (!iRes.ok) {
      setError(iRes.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(iRes.items);
    }
    if (pRes.ok) setProducts(pRes.data);
    if (cRes.ok) setCustomers(cRes.data);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const onEdit = (x) => {
    navigate(`/product-reviews?edit=${x.id}`);
  };

  const onDelete = async (id) => {
    if (!(await window.confirmDialog("Delete this product review?"))) return;
    const res = await deleteProductReview(id);
    if (!res.ok) setError(res.error ?? "Failed to delete product review");
    else await load();
  };

  const productById = useMemo(() => {
    const map = new Map();
    for (const product of products) map.set(product.id, product);
    return map;
  }, [products]);

  const customerById = useMemo(() => {
    const map = new Map();
    for (const customer of customers) map.set(customer.id, customer);
    return map;
  }, [customers]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        item,
        productName: productById.get(item.productId)?.name || `#${item.productId}`,
        customerName: customerById.get(item.customerId)?.name || `#${item.customerId}`,
        rating: item.rating ?? 0,
        review: item.review ?? "-"
      })),
    [items, productById, customerById]
  );

  const total = rows.length;
  const columns = [
      { field: "productName", headerName: "Product", flex: 1, minWidth: 150 },
      { field: "customerName", headerName: "Customer", flex: 1, minWidth: 150 },
      { field: "rating", headerName: "Rating", width: 100 },
      { field: "review", headerName: "Review", flex: 1, minWidth: 170 },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="table-actions">
            <button className="icon-button" type="button" title="Edit" onClick={() => onEdit(params.row.item)}>
              ✏️
            </button>
            <button className="icon-button danger" type="button" title="Delete" onClick={() => void onDelete(params.row.item.id)}>
              🗑️
            </button>
          </div>
        )
      }
  ];

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Product Reviews</h2>
          <p>{loading ? "Loading product reviews..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/product-reviews" className="icon-button icon-button--toolbar" title="Add product review" aria-label="Add product review">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
